import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { HeaderCategory } from "../../../../services/headerCategories";

interface HeaderCategoriesTabProps {
  categories: HeaderCategory[];
  onEdit: (category?: HeaderCategory) => void;
  onDelete: (id: string) => void;
}

const HeaderCategoriesTab: React.FC<HeaderCategoriesTabProps> = ({
  categories,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          {t("admin.contentManagement.headerCategories")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => onEdit()}
          sx={{
            background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
            boxShadow: "0 2px 8px rgba(0,123,255,0.2)",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {t("admin.contentManagement.addHeaderCategory")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {categories.map((category) => (
          <Card key={category.id} sx={{ height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1, pr: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("admin.contentManagement.fields.key")}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: { xs: "1rem", sm: "1.1rem" } }}
                  >
                    {category.key}
                  </Typography>
                </Box>
                <Chip
                  label={
                    category.is_active
                      ? t("admin.contentManagement.active")
                      : t("admin.contentManagement.inactive")
                  }
                  color={category.is_active ? "success" : "default"}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("admin.contentManagement.fields.labelEn")}
                </Typography>
                <Typography variant="body2">
                  {category.label_en || "-"}
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("admin.contentManagement.fields.labelAr")}
                </Typography>
                <Typography variant="body2">
                  {category.label_ar || "-"}
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("admin.contentManagement.fields.labelFr")}
                </Typography>
                <Typography variant="body2">
                  {category.label_fr || "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Chip
                  label={
                    t("admin.contentManagement.fields.displayOrder") +
                    ": " +
                    category.display_order
                  }
                  size="small"
                  sx={{ bgcolor: "rgba(0,0,0,0.04)" }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title={t("admin.contentManagement.edit")}>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(category)}
                      sx={{ color: "#00d4ff" }}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("admin.contentManagement.delete")}>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(category.id!)}
                      sx={{ color: "#ff3366" }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default HeaderCategoriesTab;